import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getParamAsInt } from "../../../app/utils";
import { useAppSelector } from "../../../app/hooks";
import { useScrollToSection } from "../../../app/useScrollToSection";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import { WritingTable } from "./WritingTable";

import {
	CoursePromptModel,
	PromptWritingModel,
	useGetPromptsQuery,
	useGetWritingQuery,
} from "../writingApiSlice";

export interface PromptWriting {
	coursePrompt: CoursePromptModel;
	writing?: PromptWritingModel;
	status: PromptStatus;
}

export enum PromptStatus {
	Expired = "Expired",
	Overdue = "Overdue",
	Upcoming = "Upcoming",
	Assigned = "Assigned",
	Practicing = "Practicing",
	Practice = "Practice",
	Disabled = "Disabled",
}

function statusBuilder(cp: CoursePromptModel, writing?: PromptWritingModel) {
	const dueDate = cp.dueDateUtc && new Date(cp.dueDateUtc);

	if (!cp.isEnabled) return PromptStatus.Disabled;
	if (cp.isOverdue && cp.isDueDateRequired) return PromptStatus.Expired;
	if (cp.isOverdue) return PromptStatus.Overdue;
	if (dueDate) return PromptStatus.Upcoming;
	if (cp.isAssignedToUser) return PromptStatus.Assigned;
	if (writing) return PromptStatus.Practicing;
	return PromptStatus.Practice;
}

export default function StudentWriting() {
	const [queryParams] = useSearchParams();
	const promptId = getParamAsInt(queryParams, "promptId");

	useScrollToSection();

	const [view, setView] = useState<string>("primereact");
	const [promptsWithWriting, setPromptsWithWriting] = useState<PromptWriting[]>([]);

	const getPromptsResult = useGetPromptsQuery();
	const getWritingResult = useGetWritingQuery();

	useEffect(() => {
		if (getPromptsResult.isSuccess && getWritingResult.isSuccess) {
			const promptsWithWriting = getPromptsResult.data
				.map<PromptWriting>((cp) => {
					const w = getWritingResult.data.find((w) => w.promptId == cp.prompt.promptId);
					return {
						coursePrompt: cp,
						writing: w,
						status: statusBuilder(cp, w),
					};
				})
				.filter((pw) => pw.status !== PromptStatus.Disabled);

			setPromptsWithWriting(promptsWithWriting);
		} else {
			setPromptsWithWriting([]);
		}
	}, [getPromptsResult, getWritingResult]);

	const assignedItems = promptsWithWriting
		.filter((pw) => pw.coursePrompt.isAssignedToUser || pw.coursePrompt.dueDateUtc)
		.sort((a, b) => {
			if (a.coursePrompt.dueDateUtc !== b.coursePrompt.dueDateUtc) {
				if (!a.coursePrompt.dueDateUtc) return 1;
				if (!b.coursePrompt.dueDateUtc) return -1;
				return a.coursePrompt.dueDateUtc > b.coursePrompt.dueDateUtc ? 1 : -1;
			}
			if (a.writing?.updateDateUtc !== b.writing?.updateDateUtc) {
				if (!a.writing?.updateDateUtc) return 1;
				if (!b.writing?.updateDateUtc) return -1;
				return a.writing.updateDateUtc > b.writing.updateDateUtc ? 1 : -1;
			}
			return a.coursePrompt.prompt.name > b.coursePrompt.prompt.name ? 1 : -1;
		});

	const practiceItems = promptsWithWriting
		.filter((pw) => !(pw.coursePrompt.isAssignedToUser || pw.coursePrompt.dueDateUtc))
		.sort((a, b) => {
			if (a.writing?.updateDateUtc !== b.writing?.updateDateUtc) {
				if (!a.writing?.updateDateUtc) return 1;
				if (!b.writing?.updateDateUtc) return -1;
				return a.writing.updateDateUtc > b.writing.updateDateUtc ? -1 : 1;
			}
			return a.coursePrompt.prompt.name > b.coursePrompt.prompt.name ? 1 : -1;
		});

	return (
		<>
			<section>
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1>Writing Hub</h1>
					</div>
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						options={[
							{ value: "primereact", label: "PrimeReact" },
							{ value: "raw", label: "Raw Data" },
						]}
					/>
				</div>
				<p className="mt-4">
					Welcome to the Writing Hub! From this page, you can view your writing
					assignments, start new writing, or continue earlier writing sessions. You can
					see your scores for earlier drafts of your writing and easily navigate to your
					detailed score reports.
				</p>
			</section>

			{view === "primereact" && (
				<>
					<div className="mt-8">
						<WritingTable
							id="AssignedWriting"
							type="Assignments"
							title="Assigned Writing"
							input={assignedItems}
							loading={getPromptsResult.isLoading || getWritingResult.isLoading}
							promptId={promptId}></WritingTable>
					</div>
					<div className="mt-8">
						<WritingTable
							id="PracticePrompts"
							type="Practice"
							title="Practice Prompts"
							input={practiceItems}
							loading={
								getPromptsResult.isLoading || getWritingResult.isLoading
							}></WritingTable>
					</div>
				</>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Course Prompts" result={getPromptsResult} />
					<RtkqResultViewer heading="Writing" result={getWritingResult} />
				</>
			)}
		</>
	);
}
